import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { ErrorResponse } from '@core/error/error-response.model';
import { Module } from '@api/models/UserService/Contract/module';
import { inject, Injectable } from '@angular/core';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { Observable, tap } from 'rxjs';
import { ModulesService } from '@api/services/modules.service';
import { APP_NAME } from '@core/app/app.constants';
import { ListAppSettingsResponse } from '@api/models/SettingsApi/Contract/list-app-settings-response';
import { SettingsService } from '@api/services/settings.service';
import { map } from 'rxjs/operators';
import { GetDbtimeResponse } from '@api/models/TariffEditorApi/Contract/get-dbtime-response';
import { DatabaseTimeService } from '@api/services/database-time.service';
import { CustomRxState } from '@core/state/rx.state';
import { SeoService } from '@core/seo/seo.service';


export interface IAppState {
    loading: Record<StartWithUnit, boolean>;
    errors: Record<StartWithUnit, ErrorResponse>;
    modules: Module[];
    settings: ListAppSettingsResponse[];
    defaultPhoneCode: number;
    databaseTime: GetDbtimeResponse;
}

export interface IAppAction {
    requestModules: Module[];
    requestSettings: ListAppSettingsResponse[];
}

@Injectable({
    providedIn: 'root',
})
export class AppState extends CustomRxState<IAppState> {
    readonly #modulesService: ModulesService = inject(ModulesService);
    readonly #settingsService: SettingsService = inject(SettingsService);
    readonly #databaseTimeService: DatabaseTimeService = inject(DatabaseTimeService);
    readonly #actions: RxActions<IAppAction> = new RxActionFactory<IAppAction>().create();
    readonly #seo: SeoService = inject(SeoService);

    public readonly modules$: Observable<Module[]> = this.select('modules');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');
    public readonly settings$: Observable<ListAppSettingsResponse[]> = this.select('settings');
    public readonly databaseTime$: Observable<GetDbtimeResponse> = this.selectLazy('databaseTime', this.requestDatabaseTime);

    public readonly defaultPhoneCode$: Observable<number> = this.select('settings').pipe(
        map((settings: ListAppSettingsResponse[]) =>
            settings.find((setting: ListAppSettingsResponse) => setting.name === 'DEFAULT_PHONE_COUNTRY')?.value,
        ),
        map(Number),
    );

    constructor() {
        super();

        this.connectSelectors();
        this.setDefaultState();
    }

    public requestModules(): void {
        this.hold(
            this.#modulesService.apiUsersListModulesGet({
                appName: APP_NAME,
            }),
            this.#actions.requestModules,
        );
    }

    public requestSettings(): void {
        this.hold(
            this.#settingsService.apiAppListSettingsGet(),
            this.#actions.requestSettings,
        );
    }

    public requestDatabaseTime(): Observable<GetDbtimeResponse> {
        return this.#databaseTimeService.apiTariffGetDbtimeGet();
    }

    private setDefaultState(): void {
        this.set({
            loading: null,
            errors: null,
            modules: [],
            databaseTime: null,
        });
    }

    private connectSelectors(): void {
        this.connect('settings', this.#actions.requestSettings$);
        this.connect('modules', this.#actions.requestModules$.pipe(
            tap((modules: Module[]) => this.#seo.init(modules))),
        );
    }

}
